"use client";

import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PublicPageShell } from "@/components/layout/PublicPageShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BrowseAssetCardSkeleton } from "@/components/browse/BrowseAssetCard";
import Package from "lucide-react/dist/esm/icons/package";
import Star from "lucide-react/dist/esm/icons/star";
import Users from "lucide-react/dist/esm/icons/users";
import Briefcase from "lucide-react/dist/esm/icons/briefcase";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import MessageSquare from "lucide-react/dist/esm/icons/message-square";
import TrendingUp from "lucide-react/dist/esm/icons/trending-up";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useUserFollowing, useFollowSeller, useUnfollowSeller } from "@/hooks/useUserFollowing";
import { useSellerTrustScore } from "@/hooks/useSellerTrustScore";
import { useUserBadges } from "@/hooks/useUserBadges";
import { usePublicTier } from "@/hooks/usePublicTier";
import { TIER_LIMITS } from "@/lib/subscription-tiers";
import { HireCreatorDialog, type HireMode } from "@/components/commissions/HireCreatorDialog";
import { trackSellerProfileViewed } from "@/lib/analytics";
import { logEvent } from "@/lib/events";
import { SellerProfileHeader } from "@/components/seller/SellerProfileHeader";
import { UserContextActions } from "@/components/context-actions/UserContextActions";
import { SellerAssetGrid } from "@/components/seller/SellerAssetGrid";
import { SellerReviewsTab } from "@/components/seller/SellerReviewsTab";
import { SellerAboutTab } from "@/components/seller/SellerAboutTab";
import { SellerPostsTab } from "@/components/seller/SellerPostsTab";
import { queryKeys } from "@/lib/query-keys";

const SellerProfile = ({ initialSeller }: { initialSeller?: Record<string, unknown> | null } = {}) => {
  const { username } = useParams<{ username: string }>();
  const { profile: currentUserProfile } = useProfile();
  const { openAuthModal } = useAuthModal();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (username) {
      trackSellerProfileViewed(username);
      logEvent("seller_profile_viewed", { seller_username: username }, { severity: "low" });
    }
  }, [username]);
  const { data: following } = useUserFollowing();
  const followSeller = useFollowSeller();
  const unfollowSeller = useUnfollowSeller();

  // Seeded with the row the server fetched for this page (same RPC, same
  // result for any caller). Without it the server rendered the skeleton while
  // the client — whose query is paused while the persisted cache restores —
  // rendered "Seller not found": a hydration mismatch and a 404 flash on every
  // store visit.
  const { data: seller, isPending: sellerPending, error: sellerError } = useQuery({
    queryKey: queryKeys.sellerDashboard.profile(username),
    initialData: initialSeller ?? undefined,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_seller_profile", {
        p_username: username!,
      });
      if (error) throw error;
      return data as any;
    },
    enabled: !!username,
  });

  const { data: trustScore } = useSellerTrustScore(seller?.id);
  const { data: roles } = useUserBadges(seller?.user_id);
  const { data: sellerTier } = usePublicTier(seller?.id);

  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: queryKeys.sellerDashboard.assets(seller?.id),
    queryFn: async () => {
      if (!seller?.id) return [] as any[];
      const { data, error } = await supabase.rpc("get_seller_assets", {
        p_seller_id: seller.id,
      });
      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !!seller?.id,
  });

  const { data: recentReviews } = useQuery({
    queryKey: queryKeys.sellerDashboard.recentReviews(seller?.id),
    queryFn: async () => {
      if (!seller?.id) return [] as any[];
      // `reviews` has no seller_id or reviewer_id column: the seller is reached
      // through the asset and the reviewer through user_id. The old query 400ed
      // on every store visit and the error was swallowed into an empty list, so
      // the Recent Reviews section never rendered for anyone. Approved only —
      // this is a public page.
      const { data, error } = await supabase
        .from("reviews")
        .select(
          "id, rating, content, created_at, profiles:public_profiles!reviews_user_id_fkey(display_name, avatar_url, username), asset:assets!inner(seller_id)",
        )
        .eq("asset.seller_id", seller.id)
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !!seller?.id,
  });

  const { data: stats } = useQuery({
    queryKey: queryKeys.sellerDashboard.stats(seller?.id),
    queryFn: async () => {
      if (!seller?.id) return null;

      const followersCount = await supabase
        .from("follows")
        .select("id", { count: "exact", head: true })
        .eq("following_id", seller.id);

      // An RPC, not a count on commission_contracts: that table is readable
      // only by a contract's two parties, so every visitor saw "0 jobs done".
      const { data: completedRows } = await supabase.rpc("creator_completed_commission_counts" as any, {
        p_profile_ids: [seller.id],
      });
      const completedCommissions = Number(
        (((completedRows as unknown) as { completed_count: number }[]) || [])[0]?.completed_count ?? 0,
      );

      const assetList = (assets as any[]) || [];
      const ratings = assetList
        .filter((a: any) => a.avg_rating != null)
        .map((a: any) => ({ avg: a.avg_rating, count: a.review_count }));

      const totalReviews = ratings.reduce((s: number, r: any) => s + r.count, 0);
      const weightedSum = ratings.reduce((s: number, r: any) => s + r.avg * r.count, 0);
      const averageRating = totalReviews > 0 ? weightedSum / totalReviews : null;

      const totalDownloads = assetList.reduce((s: number, a: any) => s + (a.download_count || 0), 0);

      return {
        totalAssets: assetList.length,
        followers: followersCount.count || 0,
        averageRating,
        reviewCount: totalReviews,
        commissions: completedCommissions,
        totalDownloads,
      };
    },
    enabled: !!seller?.id && !!assets,
  });

  // One dialog, opened on whichever tab the clicked button promised.
  const [hireMode, setHireMode] = useState<HireMode | null>(null);
  const isFollowing = following?.some((f) => f.seller.id === seller?.id);
  const isOwnProfile = currentUserProfile?.id === seller?.id;

  // `?hire=quote|offer` (e.g. the brand page's "Get a proposal") opens the
  // dialog on arrival — behind the auth modal when signed out. Once only.
  const [hireParamHandled, setHireParamHandled] = useState(false);
  useEffect(() => {
    if (hireParamHandled || !seller?.id || isOwnProfile) return;
    const param = new URLSearchParams(window.location.search).get("hire");
    if (param !== "quote" && param !== "offer") return;
    setHireParamHandled(true);
    if (!seller.hireable) return;
    if (!currentUserProfile) {
      openAuthModal({
        message: param === "offer" ? "Hire this creator" : "Ask this creator for a quote",
        onSuccess: () => setHireMode(param),
      });
      return;
    }
    setHireMode(param);
  }, [hireParamHandled, seller?.id, seller?.hireable, isOwnProfile, currentUserProfile, openAuthModal]);

  // The follow hooks toast success and failure themselves; this page only has
  // to refresh its follower count.
  const refreshStats = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.sellerDashboard.stats(seller?.id) });

  const handleFollow = () => {
    if (!seller?.id) return;
    followSeller.mutate(seller.id, { onSuccess: refreshStats });
  };

  const handleUnfollow = () => {
    if (!seller?.id) return;
    unfollowSeller.mutate(seller.id, { onSuccess: refreshStats });
  };

  // Pending, not isLoading: a paused query (cache restore) is pending but not
  // loading, and must still read as "not known yet" rather than "not found".
  if (!seller && sellerPending && !sellerError) {
    return (
      <PublicPageShell mainAriaLabel="Seller profile" bleed>
          <div className="h-48 md:h-72 skeleton-shimmer bg-muted" />
          <div className="container -mt-20 pb-12">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-80 shrink-0">
                <Card><CardContent className="p-6">
                  <Skeleton className="h-28 w-28 rounded-full mx-auto" />
                  <Skeleton className="h-6 w-40 mx-auto mt-4" />
                  <Skeleton className="h-4 w-56 mx-auto mt-2" />
                  <Skeleton className="h-4 w-48 mx-auto mt-1" />
                  <div className="flex gap-3 mt-4">
                    <Skeleton className="h-9 w-8 rounded-md" />
                    <Skeleton className="h-9 w-8 rounded-md" />
                    <Skeleton className="h-9 w-8 rounded-md" />
                  </div>
                  <Skeleton className="h-10 w-full mt-3" />
                </CardContent></Card>
              </div>
              <div className="flex-1 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <BrowseAssetCardSkeleton key={i} index={i} />
                ))}
              </div>
            </div>
          </div>
      </PublicPageShell>
    );
  }

  if (!seller) {
    return (
      <PublicPageShell mainAriaLabel="Seller not found" bleed>
          <div className="flex items-center justify-center min-h-[60dvh] px-4">
            <div className="text-center max-w-lg w-full">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">404</p>
              <h1 className="font-display text-2xl md:text-3xl font-bold mb-3">Creator not found</h1>
              <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                This creator doesn&apos;t exist or isn&apos;t a seller yet.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild><Link href="/browse">Browse Assets</Link></Button>
                <Button variant="outline" asChild><Link href="/">Go Home</Link></Button>
              </div>
            </div>
          </div>
      </PublicPageShell>
    );
  }

  const statItems = [
    { icon: Package, label: "Assets", value: stats?.totalAssets || 0, color: "text-primary" },
    { icon: Users, label: "Followers", value: stats?.followers || 0, color: "text-primary" },
    { icon: Star, label: "Avg Rating", value: stats?.averageRating?.toFixed(1) || "—", color: "text-warning" },
    { icon: MessageSquare, label: "Reviews", value: stats?.reviewCount || 0, color: "text-primary" },
    { icon: Briefcase, label: "Jobs Done", value: stats?.commissions || 0, color: "text-primary" },
    { icon: TrendingUp, label: "Downloads", value: stats?.totalDownloads || 0, color: "text-primary" },
  ];

  const hasBanner = !!seller.banner_url;
  // The "custom storefront theme" perk: shown only while the seller's plan
  // includes it, so a lapsed subscription doesn't keep the paid look. The DB
  // constrains the value to a hex colour, so it is safe to interpolate.
  const accent =
    sellerTier && TIER_LIMITS[sellerTier]?.customStorefrontTheme
      ? (seller as { storefront_accent?: string | null }).storefront_accent || null
      : null;

  return (
    <>
      {/* Person JSON-LD ships server-side from app/store/[username]/page.tsx — no client duplicate. */}
      <PublicPageShell mainAriaLabel="Seller profile" bleed>
        {/* ═══════════ Cover Banner ═══════════ */}
        <section className="relative h-48 md:h-64 lg:h-80 overflow-hidden">
          {hasBanner ? (
            <Image
              src={seller.banner_url!}
              alt={`${seller.display_name || seller.username}'s store banner`}
              fill
              sizes="100vw"
              className="object-cover"
            />
          ) : (
            <div
              className="absolute inset-0 bg-card border-b border-border"
              style={
                accent
                  ? { backgroundImage: `linear-gradient(135deg, ${accent}33, transparent 65%)` }
                  : undefined
              }
            />
          )}
          {accent && (
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-1"
              style={{ backgroundColor: accent }}
            />
          )}
          {/* Bottom gradient fade */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />

          {/* Floating badges on the cover (top-right, clear of overlap) */}
          <div className="absolute top-4 right-4 md:top-6 md:right-8 flex items-center gap-2 z-20">
            {/* Renders nothing for a visitor with no powers here. */}
            <UserContextActions
              profileId={seller.id}
              isOwnProfile={isOwnProfile}
              isSeller
              className="bg-background/80 backdrop-blur-md"
            />
            {seller.seller_verified && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-md border border-border/50 text-xs font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                Verified Creator
              </div>
            )}
            {seller.hireable && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/90 backdrop-blur-md text-primary-foreground text-xs font-medium">
                <Briefcase className="h-3.5 w-3.5" />
                Available for Hire
              </div>
            )}
          </div>
        </section>

        {/* ═══════════ Profile Card Overlap + Layout ═══════════ */}
        <div className="container -mt-20 md:-mt-24 pb-12 relative z-10">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* ── Sticky Sidebar ── */}
            <SellerProfileHeader
              seller={seller}
              sellerTier={sellerTier}
              roles={roles}
              trustScore={trustScore}
              isFollowing={!!isFollowing}
              isOwnProfile={isOwnProfile}
              isFollowPending={followSeller.isPending}
              isUnfollowPending={unfollowSeller.isPending}
              currentUserProfile={currentUserProfile}
              username={username!}
              onHireMeClick={() => setHireMode("quote")}
              onFollow={handleFollow}
              onUnfollow={handleUnfollow}
              onAuthRequired={(message, onSuccess, intent) =>
                openAuthModal({
                  message,
                  onSuccess,
                  intent,
                  context: {
                    title: seller.display_name || seller.username || undefined,
                    thumbnail: seller.avatar_url,
                  },
                })
              }
            />

            {/* ── Main Content ── */}
            <div className="flex-1 min-w-0 space-y-8">

              {/* ── Stats Strip ── */}
              <section className="relative z-20">
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {statItems.map((stat) => {
                    const Icon = stat.icon;
                    return (
                      <div
                        key={stat.label}
                        className="flex flex-col items-center gap-1 p-3 rounded-xl bg-card border border-border/40"
                      >
                        <Icon className={`h-4 w-4 ${stat.color}`} />
                        <span className="font-display font-bold text-lg leading-none">
                          {stat.value}
                        </span>
                        <span className="text-2xs font-mono uppercase tracking-wider text-muted-foreground">
                          {stat.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ── Assets & Portfolio ── */}
              <SellerAssetGrid
                sellerId={seller.id}
                assets={assets}
                assetsLoading={assetsLoading}
                totalAssets={stats?.totalAssets || 0}
              />

              {/* ── Recent Reviews ── */}
              {recentReviews && recentReviews.length > 0 && (
                <SellerReviewsTab
                  reviews={recentReviews}
                  totalReviewCount={stats?.reviewCount}
                />
              )}

              {/* ── Commission CTA ── */}
              <SellerAboutTab
                seller={seller}
                isOwnProfile={isOwnProfile}
                onHireClick={() => {
                  if (!currentUserProfile) {
                    openAuthModal({
                      message: "Hire this creator",
                      onSuccess: () => setHireMode("offer"),
                    });
                    return;
                  }
                  setHireMode("offer");
                }}
                onRequestQuoteClick={() => {
                  if (!currentUserProfile) {
                    openAuthModal({
                      message: "Ask this creator for a quote",
                      onSuccess: () => setHireMode("quote"),
                    });
                    return;
                  }
                  setHireMode("quote");
                }}
              />

              {/* ── Blog Posts ── */}
              <SellerPostsTab sellerId={seller.id} />
            </div>
          </div>
        </div>
      </PublicPageShell>

      {seller && (
        <HireCreatorDialog
          open={hireMode !== null}
          onOpenChange={(open) => !open && setHireMode(null)}
          initialMode={hireMode ?? "quote"}
          sellerId={seller.id}
          sellerName={seller.display_name || seller.username || "Seller"}
          startingPrice={seller.hire_starting_price}
          minBudget={seller.hire_min_budget}
        />
      )}
    </>
  );
};

export default SellerProfile;
